<template>
	<div>
		<!-- Loading -->
		<div>
			<LoadingBar :mode="estateData.mode" :loading="isLoading" />
		</div>

		<!-- Container -->
		<div
			v-if="estateData.lang"
			:class="estateData.mode !== 'latest' ? 'mt-10 lg:mt-28' : ''"
			data-sweepbright-list
		>
			<!-- Filter -->
			<div v-if="estateData.mode !== 'latest'">
				<transition
					enter-active-class="transition-all duration-300 ease-in-out"
					leave-active-class="transition-all duration-300 ease-in-out"
					enter-class="transform translate-x-full"
					enter-to-class="transform translate-x-0"
					leave-class="transform translate-x-0"
					leave-to-class="transform translate-x-full"
				>
					<div
						v-if="showFilter"
						class="fixed top-0 left-0 z-40 w-screen h-screen pt-24 overflow-hidden overflow-y-auto bg-gray-100 pb-36 lg:pb-0 lg:pt-0 lg:overflow-visible lg:w-auto lg:bg-transparent lg:static lg:h-auto"
					>
						<!-- Options -->
						<div v-if="estateData.type !== 'projects' || showFilter">
							<!-- Title -->
							<div
								class="top-0 z-10 w-full p-6 pb-3 mb-5 font-serif text-3xl text-white lg:w-auto bg-brand-500 lg:bg-transparent lg:text-brand-500 lg:leading-tight lg:p-0 lg:text-6xl"
								:class="
									estateData.type === 'projects'
										? 'fixed lg:hidden'
										: 'fixed lg:static'
								"
							>
								<h1>{{ estateData.title }}</h1>

								<button
									class="absolute top-0 right-0 p-2 mt-3.5 mr-3 text-3xl transition lg:hidden hover:opacity-80 active:opacity-50"
									@click="toggleFilter"
								>
									<i class="fal fa-times" />
								</button>
							</div>

							<div
								v-if="estateData.type !== 'spain' ? 'lg:hidden' : ''"
								class="flex-col w-full p-5 space-y-5 text-base bg-white shadow lg:shadow-none lg:bg-transparent lg:flex-row lg:items-start lg:space-x-8 lg:p-0 lg:space-y-0 lg:mb-10"
								:class="estateData.type === 'projects' ? 'lg:hidden' : 'flex'"
							>
								<!-- Search -->
								<div
									class="relative flex-1"
									:class="estateData.type === 'projects' ? 'lg:hidden' : ''"
								>
									<SearchFilter :store="store" :lang="estateData.lang" />
								</div>

								<!-- Categories -->
								<div
									class="lg:w-52"
									:class="estateData.type === 'projects' ? 'lg:hidden' : ''"
								>
									<FilterCategories
										:lang="estateData.lang"
										:locales="store.locales"
									/>
								</div>

								<!-- Min/max price -->
								<FilterMinMaxPrice
									v-if="estateData.type !== 'projects'"
									:lang="estateData.lang"
									:locales="store.locales"
									:type="estateData.type"
								/>

								<!-- Min rooms -->
								<FilterRooms
									v-if="estateData.type !== 'projects'"
									:lang="estateData.lang"
									:locales="store.locales"
								/>
							</div>
						</div>

						<!-- Sort -->
						<div
							class="mt-5 text-base bg-white shadow lg:shadow-none lg:bg-transparent lg:mb-10 lg:mt-0"
						>
							<div
								class="flex flex-col-reverse p-5 pt-0 lg:p-0 lg:flex-row lg:items-center lg:justify-between"
							>
								<!-- Button -->
								<template v-if="!store.mapMode">
									<div class="mt-5 lg:hidden">
										<button
											v-if="totalPages > 0"
											class="w-full btn btn-danger"
											@click="toggleFilter"
										>
											{{ store.locales[estateData.lang].show_results }}
											{{ totalPosts }}
										</button>
										<div v-else class="text-center text-gray-500">
											<div
												v-html="store.locales[estateData.lang].empty_filter"
											/>
											<button
												class="w-full mt-5 btn btn-danger is-disabled"
												@click="toggleFilter"
											>
												{{ store.locales[estateData.lang].back_to_overview }}
											</button>
										</div>
									</div>
								</template>

								<template v-if="store.mapMode && markersLoaded">
									<div class="mt-5 lg:hidden">
										<button
											v-if="totalPages > 0"
											class="w-full btn btn-danger"
											@click="toggleFilter"
										>
											{{ store.locales[estateData.lang].show_results }}
											({{ markers.length }})
										</button>
										<div v-else class="text-center text-gray-500">
											<div
												v-html="store.locales[estateData.lang].empty_filter"
											/>
											<button
												class="w-full mt-5 btn btn-danger is-disabled"
												@click="toggleFilter"
											>
												{{ store.locales[estateData.lang].back_to_overview }}
											</button>
										</div>
									</div>
								</template>

								<!-- Total results -->
								<div class="hidden lg:block">
									<div v-if="estateData.type !== 'projects'">
										<p v-if="totalPages > 0 && !store.mapMode" class="text-xl">
											{{ totalPosts }}
											{{ store.locales[estateData.lang].total_results }}
										</p>

										<p
											v-if="totalPages > 0 && store.mapMode && markersLoaded"
											class="text-xl"
										>
											{{ markers.length }}
											{{ store.locales[estateData.lang].total_results }}
										</p>
									</div>
								</div>

								<div
									class="flex flex-col mt-5 space-y-5 lg:items-center lg:space-x-8 lg:flex-row lg:space-y-0 lg:mt-0"
								>
									<!-- Status -->
									<FilterStatus
										:lang="estateData.lang"
										:locales="store.locales"
									/>

									<!-- Map toggle -->
									<a
										href="#"
										class="hidden lg:inline-block btn-link is-line"
										@click.prevent="loadMap"
									>
										<template v-if="!store.mapMode">
											<i class="fas fa-map-marker-alt" />
											{{ store.locales[estateData.lang].view_map }}
										</template>
										<template v-else>
											<i class="fas fa-th-large" />
											{{ store.locales[estateData.lang].view_list }}
										</template>
									</a>

									<!-- Sort -->
									<div v-if="estateData.type !== 'projects'" class="lg:w-64">
										<FilterSort
											:lang="estateData.lang"
											:locales="store.locales"
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				</transition>
			</div>

			<!-- Mobile title -->
			<h1
				v-if="estateData.mode !== 'latest'"
				class="mb-5 font-serif text-3xl text-center lg:hidden"
			>
				{{ estateData.title }}
			</h1>

			<!-- Map toggle -->
			<div v-if="estateData.mode !== 'latest'" class="mb-6 lg:hidden">
				<div class="flex items-center justify-between mb-5">
					<a href="#" class="btn-link is-line" @click.prevent="loadMap">
						<template v-if="!store.mapMode">
							<i class="fas fa-map-marker-alt" />
							{{ store.locales[estateData.lang].view_map }}
						</template>
						<template v-else>
							<i class="fas fa-th-large" />
							{{ store.locales[estateData.lang].view_list }}
						</template>
					</a>

					<a href="#" class="btn-link is-line" @click.prevent="toggleFilter">
						<i class="fas fa-filter" />
						{{ store.locales[estateData.lang].filter }}
					</a>
				</div>

				<div v-if="estateData.type !== 'projects'" class="text-center">
					<p v-if="totalPages > 0 && !store.mapMode">
						{{ totalPosts }}
						{{ store.locales[estateData.lang].total_results }}
					</p>

					<p v-if="totalPages > 0 && store.mapMode && markersLoaded">
						{{ markers.length }}
						{{ store.locales[estateData.lang].total_results }}
					</p>
				</div>
			</div>

			<!-- List -->
			<div
				v-if="!store.mapMode && estates.length > 0 && totalPages > 0"
				class="flex flex-col -m-4 lg:-m-5 lg:flex-wrap lg:flex-row"
				:class="estateData.type === 'projects' ? 'lg:w-11/12 lg:mx-auto' : ''"
			>
				<div
					v-for="estate in estates"
					:key="estate.uid"
					:class="
						estateData.type === 'projects' ? 'w-full' : 'lg:w-1/2 xl:w-1/3'
					"
				>
					<template v-if="estateData.type !== 'projects'">
						<CardDefault
							:estate="estate"
							:loading="isLoading"
							:estate-data="estateData"
							:locales="store.locales"
						/>
					</template>
					<template v-else>
						<CardAlt
							:estate="estate"
							:loading="isLoading"
							:estate-data="estateData"
							:locales="store.locales"
						/>
					</template>
				</div>
			</div>

			<!-- Empty - list -->
			<template
				v-if="!store.mapMode && estates.length === 0 && totalPages > -1"
			>
				<div
					class="p-5 mt-10 text-center border rounded-md shadow lg:p-10 border-opacity-20 border-brand-500"
				>
					<i class="mb-2 text-6xl text-gray-400 fad fa-home" />
					<p>{{ store.locales[estateData.lang].no_results }}</p>
				</div>
			</template>

			<!-- Map -->
			<div v-if="store.mapMode && markersLoaded && markers.length > 0">
				<MapView
					:marker-data="markers"
					:store="store"
					:lang="estateData.lang"
					:type="estateData.type"
				/>
			</div>

			<!-- Empty - map -->
			<template v-if="store.mapMode && markersLoaded && markers.length === 0">
				<div
					class="p-5 mt-10 text-center border rounded-md shadow lg:p-10 border-opacity-20 border-brand-500"
				>
					<i class="mb-2 text-6xl text-gray-400 fad fa-home" />
					<p>{{ store.locales[estateData.lang].no_results }}</p>
				</div>
			</template>

			<!-- Pagination -->
			<template
				v-if="
					estates.length > 0 &&
					totalPages > 0 &&
					estateData.mode !== 'latest' &&
					!store.mapMode
				"
			>
				<div class="mt-12">
					<paginate
						v-model="activePage"
						:page-count="totalPages"
						:click-handler="$sweepBrightPaginate"
						:disabled-class="'hidden'"
						:prev-link-class="'tracking-widest uppercase text-sm transition duration-200 hover:text-black focus:outline-none mr-5'"
						:next-link-class="'tracking-widest uppercase text-sm transition duration-200 hover:text-black focus:outline-none ml-5'"
						:container-class="'flex items-center space-x-3 justify-center'"
						:page-link-class="'focus:outline-none'"
						:page-class="'inline-block text-lg w-10 h-10 rounded-full flex items-center justify-center'"
						:active-class="'bg-black text-white'"
						:prev-text="'&larr; Back'"
						:next-text="'Next &rarr;'"
						:hide-prev-next="false"
					/>
				</div>
			</template>
		</div>
	</div>
</template>

<script>
import $ from "jquery"
import "url-search-params-polyfill"

import bus from "./js/bus.js"

import LoadingBar from "./ComponentsOverview/LoadingBar.vue"
import FilterCategories from "./ComponentsOverview/FilterCategories.vue"
import FilterMinMaxPrice from "./ComponentsOverview/FilterMinMaxPrice.vue"
import FilterRooms from "./ComponentsOverview/FilterRooms.vue"
import FilterStatus from "./ComponentsOverview/FilterStatus.vue"
import FilterSort from "./ComponentsOverview/FilterSort.vue"
import MapView from "./ComponentsOverview/MapView.vue"
import SearchFilter from "./ComponentsOverview/SearchFilter.vue"
import CardDefault from "./ComponentsOverview/CardDefault.vue"
import CardAlt from "./ComponentsOverview/CardAlt.vue"

const state = require("querystate")({ autoApply: false })

export default {
	components: {
		LoadingBar,
		FilterCategories,
		FilterMinMaxPrice,
		FilterRooms,
		FilterStatus,
		FilterSort,
		MapView,
		SearchFilter,
		CardDefault,
		CardAlt,
	},
	props: {
		component: {
			type: String,
			required: true,
		},
	},
	data() {
		return {
			estateData: false,
			isLoading: false,
			markersLoaded: false,
			store: {
				filters: {
					showFilter: true,
					status: false,
					categories: [],
					min_price: false,
					max_price: false,
					rooms: false,
					location: {
						region: "",
						lat: false,
						lng: false,
					},
					sort: {
						orderBy: "relevance",
						order: "desc",
					},
				},
				mapMode: false,
				locales: {
					nl: {
						filter: "Filter",
						empty_filter:
							"<p>Er zijn geen resultaten.</p><p>U kunt proberen de zoekcriteria te wijzigen.</p>",
						show_results: "Toon resultaten",
						back_to_overview: "Terug naar overzicht",
						to: "Tot",
						search: "Kies een gemeente...",
						rooms: "Slaapkamers",
						total_results: "panden gevonden",
						view_map: "Kaartweergave",
						view_list: "Lijstweergave",
						only_available: "Enkel beschikbaar",
						units_in: "units in",
						more_info: "Meer info",
						starting_from: "Prijs vanaf",
						no_results:
							"Het spijt ons, maar we konden geen panden vinden die aan uw criteria voldoen.",
						categories: {
							label: "Categorieën",
							house: "Huis",
							apartment: "Appartement",
							commercial: "Commercieel",
							land: "Grond",
							office: "Kantoor",
							parking: "Parking",
						},
						sort: {
							label: "Sorteer op",
							relevance: "Relevantie",
							price_h_l: "Prijs ↑",
							price_l_h: "Prijs ↓",
							date_h_l: "Datum ↑",
							date_l_h: "Datum ↓",
						},
						min_price: "Minimum €",
						max_price: "Maximum €",
						sold: "Verkocht",
						rented: "Verhuurd",
						option: "In optie",
						under_contract: "Onder contract",
						open_home: "Opendeur",
						let: "Te huur",
						sale: "Te koop",
					},
					fr: {
						filter: "Filtrer",
						empty_filter:
							"<p>Aucun résultat.</p><p>Vous pouvez essayer de modifier les critères de recherche.</p>",
						show_results: "Afficher les résultats",
						back_to_overview: "Retour à l'aperçu",
						to: "à",
						search: "Choisissez une commune...",
						rooms: "Chambres",
						total_results: "propriétés trouvées",
						view_map: "Vue carte",
						view_list: "Vue liste",
						only_available: "Seulement disponible",
						units_in: "unités à",
						more_info: "Plus d'info",
						starting_from: "À partir de",
						no_results:
							"Nous sommes désolés mais nous n'avons pas trouvé de propriétés correspondant à vos critères.",
						categories: {
							label: "Catégories",
							house: "Maison",
							apartment: "Appartement",
							commercial: "Commercial",
							land: "Terrain",
							office: "Bureau",
							parking: "Parking",
						},
						sort: {
							label: "Trier par",
							relevance: "Pertinence",
							price_h_l: "Prix ↑",
							price_l_h: "Prix ↓",
							date_h_l: "Date ↑",
							date_l_h: "Date ↓",
						},
						min_price: "Minimum €",
						max_price: "Maximum €",
						sold: "Vendu",
						rented: "Loué",
						option: "En option",
						under_contract: "Sous contrat",
						open_home: "Journée portes ouvertes",
					},
				},
			},
		}
	},
	mounted() {
		this.responsiveFilter()
		$(window).resize(() => {
			this.responsiveFilter()
		})

		this.estateData = window.estateData[this.component]
		this.setDefaultFilters()
		this.setCache()
		this.setRouter()
		this.setListeners()
		this.$list()
	},
	methods: {
		setRouter() {
			window.addEventListener("popstate", () => {
				this.setFilters()
				this.load()
			})
			this.setFilters()
		},
		setCache() {
			this.requestCache = JSON.parse(JSON.stringify(this.request))
			this.store.filtersCache = JSON.parse(JSON.stringify(this.store.filters))
		},
		setDefaultCache() {
			this.request = JSON.parse(JSON.stringify(this.requestCache))
			this.store.filters = JSON.parse(JSON.stringify(this.store.filtersCache))
		},
		setDefaultFilters() {
			// Set default negotiation
			switch (this.estateData.type) {
				case "let":
					this.request.filters.negotiation = "let"
					break
				case "sale":
					this.request.filters.negotiation = "sale_non_projects"
					break
				case "projects":
					this.request.filters.negotiation = "projects"
					break
				default:
					break
			}

			// Set agent filter for Spain
			if (this.estateData.type === "spain") {
				this.request.filters.agent = ["espagne@vosagences.be"]
				// this.request.filters.office = "Espagne"
			}

			// Set the status to 'available' by default
			if (
				this.estateData.mode !== "latest" &&
				(this.estateData.type === "let" || this.estateData.type === "sale")
			) {
				this.request.filters.agent = [
					"ardenne@vosagences.be",
					"ciney@vosagences.be",
					"marche@vosagences.be",
					"dinant@vosagences.be",
					"beauraing@vosagences.be",
					"immoaht@vosagences.be",
				]
				this.request.filters.status = "available"
				this.store.filters.status = true
			} else if (this.estateData.type === "rented_sold") {
				this.request.filters.status = ["sold", "rented"]
			} else {
				// Update the filter value dynamically
				this.store.filters.status = false
			}

			// Set default mode
			if (this.estateData.mode === "latest") {
				this.request.recent = 3
			}
		},
		setListeners() {
			bus.$on("setData", (item) => {
				// Location
				if (item.id === "location") {
					if (item.data.region && item.data.location) {
						state.set("region", item.data.region)
						state.set("lat", item.data.location.lat)
						state.set("lng", item.data.location.lng)
					} else {
						state.remove("region")
						state.remove("lat")
						state.remove("lng")
					}
					state.apply()
				}

				// Sort
				if (item.id === "sort") {
					state.set("order", item.data.order)
					state.set("orderBy", item.data.orderBy)
					state.apply()
				}

				// Status
				if (item.id === "status") {
					state.set("only_available", item.data)
					state.apply()
				}

				// Categories
				if (item.id === "categories") {
					if (item.data.length > 0) {
						const categories = []
						item.data.forEach((element) => {
							categories.push(element.value)
						})
						state.set("categories", categories.join("+"))
					} else {
						state.remove("categories")
					}
					state.apply()
				}

				// Min/max price
				if (item.id === "min_max_price") {
					if (item.data.min) {
						state.set("min_price", item.data.min)
					} else {
						state.remove("min_price")
					}

					if (item.data.max) {
						state.set("max_price", item.data.max)
					} else {
						state.remove("max_price")
					}
					state.apply()
				}

				// Rooms
				if (item.id === "rooms") {
					if (item.data) {
						state.set("rooms", item.data)
					} else {
						state.remove("rooms")
					}
					state.apply()
				}

				this.setFilters()
				this.load()
			})
		},
		setFilters() {
			// Reset request & filters
			this.setDefaultCache()

			const queryString = window.location.search
			const urlParams = new URLSearchParams(queryString)

			// Default filter values
			const data = JSON.parse(JSON.stringify(this.store.filtersCache))

			// Location
			if (
				urlParams.has("region") &&
				urlParams.has("lat") &&
				urlParams.has("lng")
			) {
				data.location.region = urlParams.get("region")
				data.location.lat = parseFloat(urlParams.get("lat"))
				data.location.lng = parseFloat(urlParams.get("lng"))
				this.request.filters.location.region = data.location.region
				this.request.filters.location.lat = data.location.lat
				this.request.filters.location.lng = data.location.lng
			}

			// Categories
			if (urlParams.has("categories") && urlParams.get("categories")) {
				const categories = urlParams.get("categories").split("+")
				categories.forEach((element) => {
					data.categories.push(element)
				})
				this.request.filters.category = data.categories
			}

			// Min / max price
			if (urlParams.has("max_price")) {
				data.max_price = parseInt(urlParams.get("max_price"), 10)
				this.request.filters.price.max = data.max_price
			}

			if (urlParams.has("min_price")) {
				data.min_price = parseInt(urlParams.get("min_price"), 10)
				this.request.filters.price.min = data.min_price
			}

			// Rooms
			if (urlParams.has("rooms")) {
				data.rooms = parseInt(urlParams.get("rooms"), 10)
				this.request.filters.facilities.bedrooms.min = data.rooms
			}

			// Status
			if (
				urlParams.has("only_available") &&
				urlParams.get("only_available") === "false"
			) {
				data.status = false
				this.request.filters.status = false
			} else if (
				urlParams.has("only_available") &&
				urlParams.get("only_available") === "true"
			) {
				data.status = true
				this.request.filters.status = "available"
			}

			// Sort
			if (urlParams.has("order") && urlParams.has("orderBy")) {
				data.sort = {
					order: urlParams.get("order"),
					orderBy: urlParams.get("orderBy"),
				}
				this.request.sort.order = data.sort.order
				this.request.sort.orderBy = data.sort.orderBy
			}

			// Set filter component data
			this.updateFilters("status", data)
			this.updateFilters("categories", data)
			this.updateFilters("min_price", data)
			this.updateFilters("max_price", data)
			this.updateFilters("rooms", data)
			this.updateFilters("sort", data)
			this.updateFilters("location", data)
		},
		updateFilters(id, data) {
			this.$nextTick(() => {
				bus.$emit("updateData", {
					id,
					data: data[id],
				})
			})
		},
		load() {
			if (this.store.mapMode) {
				this.$search({
					mapMode: true,
				})
			} else {
				this.$search()
			}
		},
		loadMap() {
			this.store.mapMode = !this.store.mapMode

			if (this.store.mapMode) {
				this.$search({
					mapMode: true,
				})
				window.addEventListener("loadedMap", () => {
					this.$gmapApiPromiseLazy().then(() => {
						this.markersLoaded = true
						this.isLoading = false
					})
				})
			}
		},
		toggleFilter() {
			this.showFilter = !this.showFilter
			if (this.showFilter) {
				this.$nextTick(() => {
					this.setFilters()
				})

				document
					.querySelector("body")
					.classList.add("overflow-hidden", "h-screen")
			} else {
				document
					.querySelector("body")
					.classList.remove("overflow-hidden", "h-screen")
			}
			this.$forceUpdate()
		},
		responsiveFilter() {
			if ($(window).width() < 1024) {
				this.showFilter = false
			} else {
				this.showFilter = true
				document
					.querySelector("body")
					.classList.remove("overflow-hidden", "h-screen")
			}
			this.$forceUpdate()
		},
	},
}
</script>
